import { fetchApi, postApi, ADMIN_API } from '@/api/client'
import type {
  BatchJobSummaryResponse,
  BatchJobHealthResponse,
  BatchJobHistoryResponse,
  BatchJobExecution,
} from '../types'

const BASE = `${ADMIN_API}/monitoring/batch-jobs`
const JOBS_BASE = `${ADMIN_API}/jobs`

export async function getBatchJobSummary(): Promise<BatchJobSummaryResponse> {
  return fetchApi<BatchJobSummaryResponse>(`${BASE}/summary`)
}

export async function getBatchJobHealth(): Promise<BatchJobHealthResponse> {
  return fetchApi<BatchJobHealthResponse>(`${BASE}/health`)
}

export async function getBatchJobHistory(params: {
  jobName?: string
  accountId?: number
  status?: string
  limit?: number
  offset?: number
}): Promise<BatchJobHistoryResponse> {
  const query = new URLSearchParams()
  if (params.jobName) query.set('jobName', params.jobName)
  if (params.accountId != null) query.set('accountId', String(params.accountId))
  if (params.status) query.set('status', params.status)
  query.set('limit', String(params.limit ?? 20))
  query.set('offset', String(params.offset ?? 0))
  return fetchApi<BatchJobHistoryResponse>(`${BASE}/history?${query}`)
}

export async function getBatchJobExecution(executionId: string): Promise<BatchJobExecution> {
  return fetchApi<BatchJobExecution>(`${BASE}/${executionId}`)
}

// Trigger functions
export async function triggerCustomerMetricsSync(accountId?: number): Promise<unknown> {
  const q = accountId != null ? `?accountId=${accountId}` : ''
  return postApi<unknown>(`${JOBS_BASE}/customer-metrics/sync${q}`)
}

export async function triggerCustomerMetricsRefresh(accountId?: number): Promise<unknown> {
  const q = accountId != null ? `?accountId=${accountId}` : ''
  return postApi<unknown>(`${JOBS_BASE}/customer-metrics/refresh${q}`)
}

export async function triggerCustomerMetricsRefreshAsync(accountId: number): Promise<unknown> {
  return postApi<unknown>(`${JOBS_BASE}/customer-metrics/refresh-async?accountId=${accountId}`)
}

export async function triggerAccountBaselineSync(accountId?: number): Promise<unknown> {
  const q = accountId != null ? `?accountId=${accountId}` : ''
  return postApi<unknown>(`${JOBS_BASE}/account-baseline/sync${q}`)
}

export async function triggerAccountBaselineRefresh(accountId?: number): Promise<unknown> {
  const q = accountId != null ? `?accountId=${accountId}` : ''
  return postApi<unknown>(`${JOBS_BASE}/account-baseline/refresh${q}`)
}

export async function triggerDomainExclusionListCount(): Promise<unknown> {
  return postApi<unknown>(`${JOBS_BASE}/domain-exclusion/run`)
}

export async function triggerDailyEventMetrics(): Promise<unknown> {
  return postApi<unknown>(`${JOBS_BASE}/daily-event-metrics/run`)
}

export async function triggerDailyEventMetricsBackfill(
  accId: number,
  fromDate: string,
  toDate?: string,
): Promise<unknown> {
  const q = new URLSearchParams()
  q.set('from_date', fromDate)
  if (toDate) q.set('to_date', toDate)
  q.set('accId', String(accId))
  return postApi<unknown>(`${JOBS_BASE}/daily-event-metrics/backfill?${q}`)
}

export async function triggerCampaignReconProcedure(accountId: number, date: string): Promise<unknown> {
  return postApi<unknown>(`${JOBS_BASE}/campaign-recon/procedure?accountId=${accountId}&date=${date}`)
}

export async function triggerCampaignReconSync(accountId: number, date: string): Promise<unknown> {
  return postApi<unknown>(`${JOBS_BASE}/campaign-recon/sync?accountId=${accountId}&date=${date}`)
}

export async function triggerLookupValuesSync(
  accountId?: number,
  entity?: string,
  field?: string,
): Promise<unknown> {
  const q = new URLSearchParams()
  if (accountId != null) q.set('accountId', String(accountId))
  if (entity) q.set('entity', entity)
  if (field) q.set('field', field)
  const qs = q.toString()
  return postApi<unknown>(`${JOBS_BASE}/lookup-values/sync${qs ? `?${qs}` : ''}`)
}
